import React, { useState, useEffect, useCallback } from "react";
import { Offcanvas } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/auth";

import MenuLateral from "../../Components/MenuPrincipal/MenuLateral";
import APagarGrid from "../../Components/APagarGrid/APagarGrid";
import Pagination from "../../Components/Pagination/Pagination";
import '../financeiro.css';

function APagar() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (appUserData && appUserData['ler_financeiro_a_pagar'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    const [lancamentos, setLancamentos] = useState([]);
    const [lancamentosFiltrados, setLancamentosFiltrados] = useState([]);
    const [carregando, setCarregando] = useState(true);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // =================================================================================
    // ESTADOS DE FILTRO
    // =================================================================================
    const [showFiltros, setShowFiltros] = useState(false);

    // Filtros Iniciais
    const initialState = {
        status: [], // Array de strings
        empresa: '',
        numero_doc: '',
        descricao: '',
        data_emissao_inicio: '',
        data_emissao_fim: '',
        data_vencimento_inicio: '',
        data_vencimento_fim: '',
        data_pagamento_inicio: '',
        data_pagamento_fim: '',
        valor_min: '',
        valor_max: ''
    };

    const [filtrosForm, setFiltrosForm] = useState(initialState);
    const [filtrosAplicados, setFiltrosAplicados] = useState(initialState);
    const [pesquisa, setPesquisa] = useState(''); // Barra de busca rápida
    const [textoPesquisa, setTextoPesquisa] = useState(''); // Debounced

    useEffect(() => {
        const timer = setTimeout(() => {
            setTextoPesquisa(pesquisa);
        }, 500);
        return () => clearTimeout(timer);
    }, [pesquisa]);

    const statusOptions = ['Em aberto', 'Pago'];

    // =================================================================================
    // BUSCA DE DADOS (API)
    // =================================================================================
    const buscarDados = useCallback(async () => {
        if (!currentUser) return;
        setCarregando(true);

        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            // Carrega TODOS para filtrar no front
            const url = new URL(`${API_URL}/api/financeiro/pagar`);

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Falha ao carregar lançamentos a pagar.");
            const data = await response.json();

            // Ordenação padrão (Ex: Vencimento ou ID)
            const dataOrdenada = data.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));

            setLancamentos(dataOrdenada);
            setLancamentosFiltrados(dataOrdenada);

        } catch (error) {
            Swal.fire("Erro", error.message, "error");
        } finally {
            setCarregando(false);
        }
    }, [currentUser]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    // =================================================================================
    // LÓGICA DE FILTRAGEM (FRONT-END)
    // =================================================================================
    useEffect(() => {
        let resultado = [...lancamentos];

        // 1. Pesquisa Rápida (Descrição, Empresa, Obs)
        if (textoPesquisa) {
            const termo = textoPesquisa.toLowerCase();
            resultado = resultado.filter(l =>
                (l.descricao || '').toLowerCase().includes(termo) ||
                (l.fornecedor_nome || l.empresa_nome || '').toLowerCase().includes(termo) ||
                (l.fornecedor_apelido || '').toLowerCase().includes(termo) ||
                (l.numero_doc || '').toLowerCase().includes(termo) ||
                (l.observacoes || '').toLowerCase().includes(termo)
            );
        }

        // 2. Filtros Avançados

        // Status
        if (filtrosAplicados.status.length > 0) {
            resultado = resultado.filter(l => filtrosAplicados.status.includes(l.status));
        }

        // Empresa
        if (filtrosAplicados.empresa) {
            resultado = resultado.filter(l => (l.fornecedor_nome || l.empresa_nome || '').toLowerCase().includes(filtrosAplicados.empresa.toLowerCase()));
        }

        // Descrição
        if (filtrosAplicados.descricao) {
            resultado = resultado.filter(l => (l.descricao || '').toLowerCase().includes(filtrosAplicados.descricao.toLowerCase()));
        }

        // Num Doc
        if (filtrosAplicados.numero_doc) {
            resultado = resultado.filter(l => (l.numero_doc || '').toLowerCase().includes(filtrosAplicados.numero_doc.toLowerCase()));
        }

        // Data Emissão
        if (filtrosAplicados.data_emissao_inicio) {
            const dInicio = new Date(filtrosAplicados.data_emissao_inicio);
            dInicio.setHours(0, 0, 0, 0);
            resultado = resultado.filter(l => {
                const d = new Date(l.data_emissao);
                return d >= dInicio;
            });
        }
        if (filtrosAplicados.data_emissao_fim) {
            const dFim = new Date(filtrosAplicados.data_emissao_fim);
            dFim.setHours(23, 59, 59, 999);
            resultado = resultado.filter(l => {
                const d = new Date(l.data_emissao);
                return d <= dFim;
            });
        }

        // Data Vencimento
        if (filtrosAplicados.data_vencimento_inicio) {
            const dInicio = new Date(filtrosAplicados.data_vencimento_inicio);
            dInicio.setHours(0, 0, 0, 0);
            resultado = resultado.filter(l => {
                const d = new Date(l.data_vencimento);
                return d >= dInicio;
            });
        }
        if (filtrosAplicados.data_vencimento_fim) {
            const dFim = new Date(filtrosAplicados.data_vencimento_fim);
            dFim.setHours(23, 59, 59, 999);
            resultado = resultado.filter(l => {
                const d = new Date(l.data_vencimento);
                return d <= dFim;
            });
        }

        // Data Pagamento
        if (filtrosAplicados.data_pagamento_inicio) {
            const dInicio = new Date(filtrosAplicados.data_pagamento_inicio);
            dInicio.setHours(0, 0, 0, 0);
            resultado = resultado.filter(l => {
                if (!l.data_pagamento) return false;
                const d = new Date(l.data_pagamento);
                return d >= dInicio;
            });
        }
        if (filtrosAplicados.data_pagamento_fim) {
            const dFim = new Date(filtrosAplicados.data_pagamento_fim);
            dFim.setHours(23, 59, 59, 999);
            resultado = resultado.filter(l => {
                if (!l.data_pagamento) return false;
                const d = new Date(l.data_pagamento);
                return d <= dFim;
            });
        }

        // Valor
        if (filtrosAplicados.valor_min) {
            const vMin = parseFloat(filtrosAplicados.valor_min);
            if (!isNaN(vMin)) {
                resultado = resultado.filter(l => parseFloat(l.valor) >= vMin);
            }
        }
        if (filtrosAplicados.valor_max) {
            const vMax = parseFloat(filtrosAplicados.valor_max);
            if (!isNaN(vMax)) {
                resultado = resultado.filter(l => parseFloat(l.valor) <= vMax);
            }
        }

        setLancamentosFiltrados(resultado);
        setCurrentPage(1); // Reset page on filter change

    }, [lancamentos, textoPesquisa, filtrosAplicados]);

    // Lógica de Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentLancamentos = lancamentosFiltrados.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    // =================================================================================
    // HANDLERS
    // =================================================================================

    const handleFiltroChange = (e) => {
        const { name, value, checked } = e.target;
        if (name === 'status_check') {
            const statusValue = value;
            setFiltrosForm(prev => {
                const novosStatus = checked
                    ? [...prev.status, statusValue]
                    : prev.status.filter(s => s !== statusValue);
                return { ...prev, status: novosStatus };
            });
        } else {
            setFiltrosForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAplicarFiltros = () => {
        setFiltrosAplicados(filtrosForm);
        setShowFiltros(false);
    };

    const handleLimparFiltros = () => {
        setFiltrosForm(initialState);
        setFiltrosAplicados(initialState);
        setPesquisa('');
        setTextoPesquisa('');
    };

    const handleKeyDownFiltros = (e) => {
        if (e.key === 'Enter') handleAplicarFiltros();
    };

    const hasActiveFilters =
        filtrosAplicados.status.length > 0 ||
        filtrosAplicados.empresa ||
        filtrosAplicados.descricao ||
        filtrosAplicados.numero_doc ||
        filtrosAplicados.data_emissao_inicio ||
        filtrosAplicados.data_emissao_fim ||
        filtrosAplicados.data_vencimento_inicio ||
        filtrosAplicados.data_vencimento_fim ||
        filtrosAplicados.data_pagamento_inicio ||
        filtrosAplicados.data_pagamento_fim ||
        filtrosAplicados.valor_min ||
        filtrosAplicados.valor_max;

    const handleDelete = async (id) => {
        // Validation for Delete
        if (appUserData && appUserData['excluir_financeiro_a_pagar'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Você tem certeza?',
            text: "Esta ação não pode ser desfeita!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/financeiro/pagar/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    Swal.fire('Excluído!', 'O lançamento foi excluído.', 'success');
                    buscarDados();
                } else {
                    const data = await response.json();
                    throw new Error(data.error || 'Erro ao excluir.');
                }
            } catch (error) {
                Swal.fire('Erro', error.message, 'error');
            }
        }
    };

    const handleEdit = (id) => {
        navigate(`/app/financeiroapagar/editar/${id}`);
    };

    const handlePagar = async (id) => {
        try {
            const { value: dataPag } = await Swal.fire({
                title: 'Confirmar Pagamento',
                text: 'Selecione a data do pagamento:',
                input: 'date',
                inputValue: new Date().toISOString().split('T')[0],
                showCancelButton: true,
                confirmButtonText: 'Confirmar',
                cancelButtonText: 'Cancelar'
            });

            if (dataPag) {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;

                // 1. Busca item atual para manter integridade
                const getResp = await fetch(`${API_URL}/api/financeiro/pagar`, { headers: { 'Authorization': `Bearer ${token}` } });
                const data = await getResp.json();
                const item = data.find(i => String(i.id) === String(id));

                if (!item) throw new Error("Item não encontrado.");

                const payload = { ...item, status: 'Pago', data_pagamento: dataPag };

                const response = await fetch(`${API_URL}/api/financeiro/pagar/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error("Erro ao salvar pagamento.");

                Swal.fire('Sucesso!', 'Pagamento registrado.', 'success');
                buscarDados();
            }
        } catch (error) {
            Swal.fire('Erro', error.message, 'error');
        }
    };

    const handleEstornar = async (id) => {
        const { isConfirmed } = await Swal.fire({
            title: 'Confirmar Estorno?',
            text: "O pagamento será revertido e o status voltará para 'Em aberto'.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, estornar',
            cancelButtonText: 'Cancelar'
        });

        if (isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;

                // 1. Busca item atual
                const getResp = await fetch(`${API_URL}/api/financeiro/pagar`, { headers: { 'Authorization': `Bearer ${token}` } });
                const data = await getResp.json();
                const item = data.find(i => String(i.id) === String(id));

                if (!item) throw new Error("Item não encontrado.");

                const payload = { ...item, status: 'Em aberto', data_pagamento: null };

                const response = await fetch(`${API_URL}/api/financeiro/pagar/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error("Erro ao estornar.");

                Swal.fire('Sucesso', 'Estorno realizado.', 'success');
                buscarDados();
            } catch (error) {
                Swal.fire('Erro', error.message, 'error');
            }
        }
    };

    return (
        <div className="financeiro-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar">
                <div className="financeiro-header">
                    <h1 className="financeiro-title">
                        <i className="fas fa-money-bill-wave text-primary"></i>
                        Contas a <span className="highlight">Pagar</span>
                    </h1>
                </div>

                <main className="financeiro-content">
                    <div className="financeiro-controls">
                        <button className="btn-primary-custom me-3" onClick={() => {
                            if (appUserData && appUserData['cadastrar_financeiro_a_pagar'] === false) {
                                Swal.fire({
                                    icon: 'error',
                                    title: 'Acesso Negado',
                                    text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                    confirmButtonColor: '#2185c6'
                                });
                                return;
                            }
                            navigate('/app/financeiroapagar/novo');
                        }}>
                            <i className="fas fa-plus"></i>Novo Lançamento
                        </button>

                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por Descrição ou Empresa..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => { if (e.key === 'Enter') setTextoPesquisa(pesquisa); }}
                            />
                        </div>

                        <button
                            className="btn-icon-custom position-relative"
                            onClick={() => setShowFiltros(true)}
                            title="Filtros Avançados"
                        >
                            <i className="fas fa-filter"></i>
                            {hasActiveFilters && (
                                <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
                                    <span className="visually-hidden">Filtros ativos</span>
                                </span>
                            )}
                        </button>
                        <button className="btn-icon-custom ms-2" onClick={buscarDados} title="Atualizar Lista">
                            <i className={`fas fa-sync-alt ${carregando ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>

                    {carregando ? (
                        <div className="loading-container">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <APagarGrid
                                arrayPagar={currentLancamentos}
                                onPagar={handlePagar}
                                onDelete={handleDelete}
                                onEstornar={handleEstornar}
                                onEdit={handleEdit}
                                userPermissions={appUserData}
                            />
                            <Pagination
                                totalItems={lancamentosFiltrados.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </main>

                {/* --- SIDEBAR DE FILTROS AVANÇADOS --- */}
                <Offcanvas show={showFiltros} onHide={() => setShowFiltros(false)} placement="end">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title><i className="fas fa-filter me-2"></i>Filtros Avançados</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <div className="d-grid gap-3" onKeyDown={handleKeyDownFiltros}>

                            <div className="d-grid gap-2">
                                <button className="btn btn-primary-custom" onClick={handleAplicarFiltros}>
                                    <i className="fas fa-check me-2"></i>Aplicar Filtros
                                </button>
                                <button className="btn btn-outline-secondary" onClick={handleLimparFiltros}>
                                    <i className="fas fa-eraser me-2"></i>Limpar Filtros
                                </button>
                            </div>
                            <hr className="m-0" />

                            {/* Status */}
                            <div className="card bg-light border-0">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-2 text-muted fw-bold">Status</h6>
                                    <div className="d-flex flex-column gap-2">
                                        {statusOptions.map((opt) => (
                                            <div className="form-check" key={opt}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="status_check"
                                                    value={opt}
                                                    id={`check-status-${opt}`}
                                                    checked={filtrosForm.status.includes(opt)}
                                                    onChange={handleFiltroChange}
                                                />
                                                <label className="form-check-label ms-2" htmlFor={`check-status-${opt}`}>{opt}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Empresa */}
                            <div>
                                <label className="form-label small fw-bold">Empresa</label>
                                <input type="text" className="form-control" name="empresa" value={filtrosForm.empresa} onChange={handleFiltroChange} placeholder="Nome da empresa..." />
                            </div>

                            {/* Descrição */}
                            <div>
                                <label className="form-label small fw-bold">Descrição</label>
                                <input type="text" className="form-control" name="descricao" value={filtrosForm.descricao} onChange={handleFiltroChange} placeholder="Descrição..." />
                            </div>

                            {/* Pedido */}
                            <div>
                                <label className="form-label small fw-bold">Num. Documento</label>
                                <input type="text" className="form-control" name="numero_doc" value={filtrosForm.numero_doc} onChange={handleFiltroChange} placeholder="Nº Documento..." />
                            </div>

                            {/* Data Emissão */}
                            <div className="row g-2">
                                <label className="form-label small fw-bold mb-0">Emissão</label>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_emissao_inicio" value={filtrosForm.data_emissao_inicio} onChange={handleFiltroChange} title="De" />
                                </div>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_emissao_fim" value={filtrosForm.data_emissao_fim} onChange={handleFiltroChange} title="Até" />
                                </div>
                            </div>

                            {/* Data Vencimento */}
                            <div className="row g-2">
                                <label className="form-label small fw-bold mb-0">Vencimento</label>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_vencimento_inicio" value={filtrosForm.data_vencimento_inicio} onChange={handleFiltroChange} title="De" />
                                </div>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_vencimento_fim" value={filtrosForm.data_vencimento_fim} onChange={handleFiltroChange} title="Até" />
                                </div>
                            </div>

                            {/* Data Pagamento */}
                            <div className="row g-2">
                                <label className="form-label small fw-bold mb-0">Pagamento</label>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_pagamento_inicio" value={filtrosForm.data_pagamento_inicio} onChange={handleFiltroChange} title="De" />
                                </div>
                                <div className="col-6">
                                    <input type="date" className="form-control" name="data_pagamento_fim" value={filtrosForm.data_pagamento_fim} onChange={handleFiltroChange} title="Até" />
                                </div>
                            </div>

                            {/* Valor */}
                            <div className="row g-2">
                                <label className="form-label small fw-bold mb-0">Valor</label>
                                <div className="col-6">
                                    <input type="number" className="form-control" name="valor_min" value={filtrosForm.valor_min} onChange={handleFiltroChange} placeholder="Min" step="0.01" />
                                </div>
                                <div className="col-6">
                                    <input type="number" className="form-control" name="valor_max" value={filtrosForm.valor_max} onChange={handleFiltroChange} placeholder="Max" step="0.01" />
                                </div>
                            </div>



                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

            </div>
        </div>
    );
}

export default APagar;